import { useEffect, useState } from 'react';
import { getToken } from '../../api/http';
import { chatApi } from './chatApi';

/* Chat uživo: JEDNA Server-Sent Events veza za celu aplikaciju (/API/chat/tok), otvorena dok je korisnik prijavljen —
   broj nepročitanih na meniju i zvuk nove poruke rade na svakoj stranici, ne samo na ekranu Chat.
   Događaji sa servera (utils/chat/tok.js): poruke, procitano, kuca, zakacene, razgovori, uklonjen, prisustvo.
   Prisustvo: posle 15 min bez miša/tastature javlja se "odsutan", pri prvoj radnji ponovo "online". */

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const PRAG_ODSUTAN = 15 * 60 * 1000;

let izvor = null;
let token = null;
const slusaoci = new Map(); // dogadjaj -> Set(fn)
let neprocitano = 0;
const pretplatniciNeprocitano = new Set();
let prisustvo = {}; // korisnikId -> { stanje, poslednja }
const pretplatniciPrisustvo = new Set();

const objaviNeprocitano = (n) => { neprocitano = Math.max(0, n); pretplatniciNeprocitano.forEach((f) => f(neprocitano)); };
export const postaviNeprocitano = objaviNeprocitano;
export const osveziNeprocitano = () => chatApi.neprocitano().then((r) => objaviNeprocitano(r ? r.neprocitano : 0)).catch(() => {});

/** prijavljeni korisnik (id iz tokena) */
export const mojId = () => {
    try {
        const t = getToken();
        return t ? Number(JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).unique_name) : null;
    } catch { return null; }
};

/* zvuk nove poruke (dva tona, kao obaveštenje) */
let audio = null;
export const zvukPoruke = () => {
    try {
        if (!audio) audio = new (window.AudioContext || window.webkitAudioContext)();
        const t0 = audio.currentTime;
        [[440, 0], [660, 0.13]].forEach(([f, d]) => {
            const o = audio.createOscillator(); const g = audio.createGain();
            o.type = 'sine'; o.frequency.value = f;
            g.gain.setValueAtTime(0.0001, t0 + d);
            g.gain.exponentialRampToValueAtTime(0.18, t0 + d + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + d + 0.18);
            o.connect(g); g.connect(audio.destination);
            o.start(t0 + d); o.stop(t0 + d + 0.2);
        });
    } catch { /* bez zvuka */ }
};

/** ekran Chat javlja koji je razgovor otvoren i vidljiv (nova poruka u njemu ne povećava brojač) */
let otvoren = null;
export const postaviOtvoren = (razgovorId) => { otvoren = razgovorId; };

const emituj = (dogadjaj, podaci) => (slusaoci.get(dogadjaj) || new Set()).forEach((f) => { try { f(podaci); } catch { /* slušalac */ } });

const naPoruke = (podaci) => {
    const ja = mojId();
    /* nove tuđe poruke (server označava "nova"; izmena, reakcija, glas — nisu nove) */
    const nove = podaci.nova ? (podaci.poruke || []).filter((p) => p.korisnikId !== ja && p.tip !== 'system') : [];
    if (nove.length) {
        zvukPoruke();
        const uOtvorenom = nove.filter((p) => p.razgovorId === otvoren && document.visibilityState === 'visible' && document.hasFocus());
        if (nove.length > uOtvorenom.length) objaviNeprocitano(neprocitano + (nove.length - uOtvorenom.length));
    }
    emituj('poruke', podaci);
};

const poveži = () => {
    const t = getToken();
    if (izvor && t === token) return;
    if (izvor) { izvor.close(); izvor = null; }
    token = t;
    if (!t) { objaviNeprocitano(0); return; }
    izvor = new EventSource(`${BASE}/API/chat/tok?t=${encodeURIComponent(t)}`);
    izvor.onopen = () => { osveziNeprocitano(); emituj('povezan', {}); };
    const json = (fn) => (e) => { try { fn(JSON.parse(e.data)); } catch { /* neispravna poruka */ } };
    izvor.addEventListener('poruke', json(naPoruke));
    ['procitano', 'kuca', 'zakacene', 'razgovori', 'uklonjen'].forEach((ev) => izvor.addEventListener(ev, json((p) => {
        if (ev === 'procitano' && p.korisnikId === mojId()) osveziNeprocitano();
        if (ev === 'razgovori' || ev === 'uklonjen') osveziNeprocitano();
        emituj(ev, p);
    })));
    izvor.addEventListener('prisustvo', json((p) => {
        const novo = p.sve ? {} : { ...prisustvo };
        (p.stanja || []).forEach((s) => {
            if (s.stanje === 'offline') delete novo[s.korisnikId];
            else novo[s.korisnikId] = { stanje: s.stanje, poslednja: s.poslednja };
        });
        prisustvo = novo;
        pretplatniciPrisustvo.forEach((f) => f(prisustvo));
        emituj('prisustvo', p);
    }));
};

/* odsutnost: 15 min bez radnje */
let poslednjaRadnja = Date.now();
let odsutan = false;
let pracenje = false;
const radnja = () => {
    poslednjaRadnja = Date.now();
    if (odsutan) { odsutan = false; chatApi.prisustvo('online').catch(() => {}); }
};
const pratiAktivnost = () => {
    if (pracenje) return;
    pracenje = true;
    ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'focus'].forEach((ev) => window.addEventListener(ev, radnja, { passive: true }));
    setInterval(() => {
        if (!odsutan && getToken() && Date.now() - poslednjaRadnja > PRAG_ODSUTAN) {
            odsutan = true;
            chatApi.prisustvo('odsutan').catch(() => {});
        }
    }, 30000);
};

/** pokreće vezu (Shell, posle prijave) i prati odjavu/prijavu */
export function useChatTok() {
    useEffect(() => {
        poveži();
        pratiAktivnost();
        const sync = () => poveži();
        window.addEventListener('tacho-auth', sync);
        return () => window.removeEventListener('tacho-auth', sync);
    }, []);
}

/** pretplata na događaj chata; vraća funkciju za odjavu */
export const naChat = (dogadjaj, fn) => {
    if (!slusaoci.has(dogadjaj)) slusaoci.set(dogadjaj, new Set());
    slusaoci.get(dogadjaj).add(fn);
    return () => slusaoci.get(dogadjaj).delete(fn);
};

export function useNeprocitano() {
    const [n, setN] = useState(neprocitano);
    useEffect(() => { pretplatniciNeprocitano.add(setN); setN(neprocitano); return () => pretplatniciNeprocitano.delete(setN); }, []);
    return n;
}

export function usePrisustvo() {
    const [p, setP] = useState(prisustvo);
    useEffect(() => { pretplatniciPrisustvo.add(setP); setP(prisustvo); return () => pretplatniciPrisustvo.delete(setP); }, []);
    return p;
}
